import { Text, View, Pressable, RefreshControl, ScrollView } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter, useNavigation, useSegments } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { deleteSmallGroupType } from '@/data/smallGroups';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { showAlert } from '@/utils/showAlert';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import Toast from 'react-native-toast-message';
import SearchBox from '@/components/General/inputs/SearchBox';

const TypeCard = ({ type, canEdit, canDelete, onEdit, onDelete, onView }) => {
    const menuOptions = useMemo(() => {
        const opts = [];
        if (canEdit) {
            opts.push({
                name: "Edit Type",
                icon: dropdownIcons.edit,
                onSelect: () => onEdit(type),
            });
        }
        if (canDelete) {
            opts.push({
                name: "Delete Type",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Small Group Type?",
                    subtitle: `Are you sure you want to delete "${type.name}"? All small groups of this type will also be deleted.`,
                    isDestructive: true,
                    confirmText: "Delete",
                    onConfirm: () => onDelete(type.id),
                }),
            });
        }
        return opts;
    }, [type, canEdit, canDelete, onEdit, onDelete]);

    return (
        <View style={styles.card}>
            <View style={styles.cardHeader}>
                <View style={styles.iconBadge}>
                    <Unicon name="book" size={20} colorVariant="secondary" />
                </View>
                <View style={styles.cardInfo}>
                    <Text numberOfLines={1} style={styles.cardTitle}>{type.name}</Text>
                    {type.description ? (
                        <Text numberOfLines={2} style={styles.cardDescription}>{type.description}</Text>
                    ) : null}
                </View>
                {menuOptions.length > 0 && (
                    <DropdownMenu testID={`TypeMenu-${type.id}`} options={menuOptions} />
                )}
            </View>
            <Pressable
                style={styles.cardPressable}
                onPress={() => onView(type)}
            >
                <View style={styles.metaRow}>
                    <Unicon name="people-outline" size={14} colorVariant="textTertiary" />
                    <Text style={styles.metaText}>View {type.name} groups</Text>
                </View>
            </Pressable>
        </View>
    );
};

export default function SmallGroupTypesScreen() {
    const [searchText, setSearchText] = useState("");
    const queryClient = useQueryClient();
    const navigation = useNavigation();
    const router = useRouter();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: smallGroupTypes, isFetching } = Query.smallGroupTypes.all({});

    const canCreate = loggedInUser?.can?.("Create Small Group Types");
    const canEdit = loggedInUser?.can?.("Edit Small Group Types");
    const canDelete = loggedInUser?.can?.("Delete Small Group Types");

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroupTypes.all() });
        queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
        queryClient.invalidateQueries({ queryKey: ['people'] });
    }, [queryClient]);

    useEffect(() => {
        const options = [];
        if (canCreate) {
            options.push({
                name: "New Small Group Type",
                icon: dropdownIcons.add,
                onSelect: () => router.push("/smallgroups/types/create"),
            });
        }
        navigation.setOptions({
            headerRight: options.length > 0 ? () => <DropdownMenu testID="HeaderMenu" options={options} /> : null,
        });
    }, [navigation, canCreate, router]);

    const deleteMutation = useMutation({
        mutationFn: deleteSmallGroupType,
        onSuccess: () => {
            Toast.delayShow({ type: "success", text2: "Small Group Type deleted successfully" });
            refresh();
        },
    });

    const filteredTypes = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return smallGroupTypes || [];
        return (smallGroupTypes || []).filter(
            (t) => t.name?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
        );
    }, [smallGroupTypes, searchText]);

    const segments = useSegments();
    const currentTab = segments[0] === '(tabs)' && segments[1] ? segments[1] : '(home)';

    const handleEdit = useCallback((type) => {
        router.push({ pathname: "/smallgroups/types/create", params: { id: type.id } });
    }, [router]);

    const handleDelete = useCallback((id) => {
        deleteMutation.mutate(id);
    }, [deleteMutation]);

    const handleView = useCallback((type) => {
        router.push(`/(tabs)/${currentTab}/smallgroups?typeID=${type.id}`);
    }, [router, currentTab]);

    return (
        <View style={styles.container}>
            <View style={styles.contentWrapper}>
                <View style={styles.searchContainer}>
                    <SearchBox
                        testID="SmallGroupTypes"
                        searchText={searchText}
                        onChangeText={setSearchText}
                        placeholder="Search small group types..."
                    />
                </View>
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={styles.listContent}
                    refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {isFetching && (!smallGroupTypes || smallGroupTypes.length === 0) ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" />
                        </View>
                    ) : filteredTypes.length === 0 ? (
                        <EmptyListPlaceholder
                            message={searchText
                                ? "No types match your search"
                                : "No small group types yet. Create one from the menu above."
                            }
                        />
                    ) : (
                        filteredTypes.map((type) => (
                            <TypeCard
                                key={type.id}
                                type={type}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onView={handleView}
                            />
                        ))
                    )}
                    <View style={styles.listFooterSpacer} />
                </ScrollView>
            </View>
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    contentWrapper: { flex: 1, maxWidth: 760, width: "100%", alignSelf: "center" },
    searchContainer: { paddingHorizontal: 8, paddingBottom: 4, paddingTop: 12 },
    list: { flex: 1, paddingHorizontal: 8 },
    listContent: { paddingTop: 8, paddingBottom: 30 },
    listFooterSpacer: { paddingBottom: 30 },
    loadingContainer: { padding: 40, alignItems: "center", justifyContent: "center" },

    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        marginTop: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: "hidden",
    },
    cardHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
    },
    iconBadge: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.secondaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    cardInfo: { flex: 1 },
    cardTitle: {
        fontSize: 16,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    cardDescription: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    cardPressable: {
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        backgroundColor: theme.colors.surfaceVariant,
        _web: {
            transition: "background-color 0.15s ease-in-out",
            _hover: { backgroundColor: theme.colors.hover },
            cursor: "pointer",
        },
    },
    metaRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    metaText: {
        fontSize: 13,
        color: theme.colors.primary,
        fontFamily: theme.fonts.heading,
    },
}));
